/**
 * Loaded once at startup from GET /api/diagnostics/logging-config
 * (same values as config/csrp-logging.json on the server).
 */

const DEFAULTS = {
  enabled: false,
  logSuccess: false,
  logFailure: false,
  pageLog: true,
  frontEndLogPath: "frontend/FrontEndLog",
};

let config = { ...DEFAULTS };
let loadPromise = null;

const trimTrailingSlash = (s) => String(s || "").replace(/\/+$/, "");

const getBootstrapApiOrigin = () => {
  const fromEnv = trimTrailingSlash(process.env.REACT_APP_LOCAL_API_ORIGIN);
  if (fromEnv) return fromEnv;
  const port = String(process.env.REACT_APP_LOCAL_API_PORT || "5001").replace(/[^0-9]/g, "") || "5001";
  if (typeof window === "undefined") {
    return `http://localhost:${port}`;
  }
  const { hostname, protocol } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    if (protocol === "https:") {
      const httpsPort =
        String(process.env.REACT_APP_LOCAL_API_HTTPS_PORT || "7049").replace(/[^0-9]/g, "") || "7049";
      return `https://${hostname}:${httpsPort}`;
    }
    return `http://${hostname}:${port}`;
  }
  const scheme = protocol === "https:" ? "https" : "http";
  return `${scheme}://${hostname}:${port}`;
};

export const getCsrpLoggingConfig = () => config;

const resolveLoggingConfigUrl = async () => {
  try {
    const { getApiBaseUrl } = await import("./api");
    let base = trimTrailingSlash(getApiBaseUrl() || "");
    // CRA dev proxy: axios uses same-origin ""; fetch logging-config the same way.
    if (!base && typeof window !== "undefined") {
      base = trimTrailingSlash(window.location.origin);
    }
    if (!base) {
      base = getBootstrapApiOrigin();
    }
    return `${base}/api/diagnostics/logging-config`;
  } catch {
    return `${getBootstrapApiOrigin()}/api/diagnostics/logging-config`;
  }
};

export const loadCsrpLoggingConfig = async () => {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const url = await resolveLoggingConfigUrl();
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        config = {
          enabled: data?.enabled === true,
          logSuccess: data?.logSuccess === true,
          logFailure: data?.logFailure === true,
          pageLog: data?.pageLog !== false,
          frontEndLogPath: String(data?.frontEndLogPath || DEFAULTS.frontEndLogPath).trim(),
        };
      } else if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("[CSRP] logging-config HTTP", res.status, url);
      }
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("[CSRP] logging-config fetch failed — using defaults (no success logs)", err);
      }
      config = { ...DEFAULTS };
    }
    return config;
  })();
  return loadPromise;
};
